import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import ThemeToggle from './ThemeToggle';
import {
  LayoutDashboard, ShoppingCart, Package, Users, FileText,
  Settings, LogOut, Menu, ChevronRight, User, Building2, RotateCcw, Boxes, Briefcase, Receipt, MoreHorizontal,
  CalendarClock, CircleAlert, Crown
} from 'lucide-react';

const formatSubscriptionDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
};

const SubscriptionStatusBar = ({ subscription, isOwner }) => {
  if (!subscription) return null;

  const status = subscription.subscription_status || 'trialing';
  const isTrial = status === 'trialing';
  const isPaid = status === 'active';
  const isUrgent = isTrial && subscription.days_remaining != null && subscription.days_remaining <= 3;
  const dateLabel = formatSubscriptionDate(subscription.period_ends_at);
  const Icon = isTrial ? CalendarClock : isPaid ? Crown : CircleAlert;
  const containerClass = isUrgent
    ? 'border-destructive/30 bg-destructive/10 text-destructive'
    : isTrial
      ? 'border-warning/30 bg-warning/10 text-foreground'
      : isPaid
        ? 'border-primary/25 bg-primary/10 text-foreground'
        : 'border-destructive/30 bg-destructive/10 text-destructive';

  let title = 'Subscription needs attention';
  let detail = status.replace('_', ' ');
  if (isTrial) {
    title = 'Free trial';
    detail = subscription.days_remaining == null
      ? 'Trial end date unavailable'
      : `${subscription.days_remaining} ${subscription.days_remaining === 1 ? 'day' : 'days'} left`;
  } else if (isPaid) {
    title = `${subscription.plan_code && subscription.plan_code !== 'trial' ? subscription.plan_code : 'Paid'} plan`;
    detail = subscription.days_remaining == null
      ? 'Account active'
      : `${subscription.days_remaining} ${subscription.days_remaining === 1 ? 'day' : 'days'} until renewal`;
  }

  return (
    <div className={`border-y px-4 py-2.5 sm:px-6 ${containerClass}`} role="status" aria-live="polite">
      <div className="mx-auto flex max-w-screen-2xl items-center gap-3">
        <Icon className="h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1 text-sm">
          <span className="font-semibold capitalize">{title}</span>
          <span className="mx-2 text-current/40">•</span>
          <span>{detail}</span>
          {dateLabel && <span className="hidden sm:inline"> · {isTrial ? 'Ends' : 'Renews'} {dateLabel}</span>}
        </div>
        {isOwner && (
          <Button variant="ghost" size="sm" asChild className="h-8 shrink-0 px-2 text-current hover:bg-background/40 hover:text-current">
            <Link to="/settings">Manage</Link>
          </Button>
        )}
      </div>
    </div>
  );
};

const MainLayout = ({ children }) => {
  const { user, businessSettings, logout, isOwner, permissions, api } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    let active = true;
    api.get('/settings/subscription')
      .then((response) => {
        if (active) setSubscription(response.data);
      })
      .catch(() => {
        if (active) setSubscription(null);
      });
    return () => { active = false; };
  }, [api]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['owner'] },
    { name: 'POS', href: '/pos', icon: ShoppingCart, roles: ['owner', 'manager', 'cashier'] },
    { name: 'Sales', href: '/sales-history', icon: Receipt, roles: ['owner', 'manager', 'cashier'] },
    { name: 'Refunds', href: '/refunds', icon: RotateCcw, roles: ['owner', 'manager', 'cashier'], permissionKey: 'issue_refunds' },
    { name: 'Inventory', href: '/inventory', icon: Boxes, roles: ['owner', 'manager', 'cashier'] },
    { name: 'Operations', href: '/operations', icon: Briefcase, roles: ['owner', 'manager'] },
    { name: 'Products', href: '/products', icon: Package, roles: ['owner', 'manager', 'cashier'] },
    { name: 'Customers', href: '/customers', icon: Users, roles: ['owner', 'manager', 'cashier'] },
    { name: 'Reports', href: '/reports', icon: FileText, roles: ['owner', 'manager'], permissionKey: 'view_reports' },
    { name: 'Settings', href: '/settings', icon: Settings, roles: ['owner'] },
  ];

  const hasPermission = (key) => {
    if (isOwner) return true;
    if (!key) return true;
    if (!permissions) return true;
    return permissions[key] !== false;
  };

  const filteredNav = navigation.filter((item) => item.roles.includes(user?.role) && hasPermission(item.permissionKey));
  const mobilePrimaryNav = filteredNav.filter((item) => ['Dashboard', 'POS', 'Sales'].includes(item.name));

  const isPathActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }

    return location.pathname === href;
  };

  const NavLink = ({ item, mobile = false }) => {
    const isActive = isPathActive(item.href);
    const Icon = item.icon;

    return (
      <Link
        to={item.href}
        onClick={() => mobile && setMobileMenuOpen(false)}
        className={`
          flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200
          ${isActive 
            ? 'bg-primary text-primary-foreground neon-green' 
            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }
          ${mobile ? 'text-lg' : ''}
        `}
        data-testid={`nav-${item.name.toLowerCase()}`}
      >
        <Icon className="h-5 w-5" />
        <span className="font-medium">{item.name}</span>
        {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
      </Link>
    );
  };

  const SidebarContent = ({ mobile = false }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img
            src="/survipos%20logo.png"
            alt="SurviPOS logo"
            className="h-10 w-10 object-cover"
          />
          <div>
            <h1 className="font-heading text-xl font-bold text-primary">SurviPOS</h1>
            <p className="text-xs text-muted-foreground">by Kareem Data Labs</p>
          </div>
        </Link>
      </div>

      {/* Business Info */}
      {businessSettings && (
        <div className="px-4 mb-4">
          <div className="p-3 rounded-lg bg-muted/25">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium truncate">{businessSettings.business_name}</span>
            </div>
            <Badge variant="outline" className="mt-2 text-xs">
              {businessSettings.tax_type === 'vat_registered' ? 'VAT Registered' : 'Presumptive Tax'}
            </Badge>
          </div>
        </div>
      )}

      {/* Navigation */}
      <ScrollArea className="flex-1 px-4 py-4">
        <nav className="space-y-1">
          {filteredNav.map((item) => (
            <NavLink key={item.name} item={item} mobile={mobile} />
          ))}
        </nav>
      </ScrollArea>

      {/* User Section */}
      <div className="p-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-full justify-start gap-3 h-auto py-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div className="text-left flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5">
              <ThemeToggle className="w-full justify-start" variant="ghost" />
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex lg:h-screen lg:overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-card/35 lg:h-screen">
        <SidebarContent />
      </aside>

      {/* Mobile Header & Content */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Mobile Header */}
        <header className="lg:hidden flex items-center justify-between p-4 bg-card/35">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <SidebarContent mobile />
            </SheetContent>
          </Sheet>

          <Link to="/dashboard" className="flex items-center gap-2">
            <img
              src="/survipos%20logo.png"
              alt="SurviPOS logo"
              className="h-8 w-8 object-cover"
            />
            <span className="font-heading text-lg font-bold text-primary">SurviPOS</span>
          </Link>

          <ThemeToggle size="icon" showLabel={false} />
        </header>

        <SubscriptionStatusBar subscription={subscription} isOwner={isOwner} />

        {/* Main Content */}
        <main
          className={`flex-1 min-h-0 overflow-auto p-4 pb-18 lg:p-6 ${
            location.pathname === '/pos' ? 'lg:overflow-hidden' : ''
          }`}
        >
          {children}
        </main>

        <nav className="lg:hidden sticky bottom-0 z-40 border-t border-border/30 bg-background/95 backdrop-blur-xl">
          <div className="grid grid-cols-4 px-2 py-1.5">
            {mobilePrimaryNav.map((item) => {
              const Icon = item.icon;
              const isActive = isPathActive(item.href);

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex flex-col items-center justify-center gap-1 rounded-xl px-2 py-1.5 text-xs font-medium transition-colors ${
                    isActive ? 'text-primary' : 'text-muted-foreground'
                  }`}
                  data-testid={`mobile-bottom-nav-${item.name.toLowerCase()}`}
                >
                  <Icon className={`h-5 w-5 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span>{item.name}</span>
                </Link>
              );
            })}

            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="flex flex-col items-center justify-center gap-1 rounded-xl px-2 py-1.5 text-xs font-medium text-muted-foreground transition-colors"
              data-testid="mobile-bottom-nav-more"
            >
              <MoreHorizontal className="h-5 w-5" />
              <span>More</span>
            </button>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default MainLayout;
